import {
  Box, Grid, TextField, Typography, Button, Paper, FormControl, InputLabel,
  Select, MenuItem, Chip, Divider, IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { CreateStepperRaffleProps } from "../../../../domain/CreateStepperRaffleProps";
import { BANK_ACCOUNTS, BankAccountItem } from "../../../../constants/bankAccounts";
import { banks, getIconFromBankId } from "../../../../constants/banks";
import { useGetLastActiveRaffleQuery, useGetRafflesQuery } from "../../../../store/api";

// Copia editable: los objetos de la caché de RTK Query están congelados
const cloneBankAccounts = (accounts: BankAccountItem[]): BankAccountItem[] =>
  accounts.map((account) => ({
    ...account,
    conversion: account.conversion ? { ...account.conversion } : undefined,
  }));


export const BankAccountSteps = ({
  resource,
  setResource,
  setDisableNext,
  accountId,
}: CreateStepperRaffleProps) => {
  const initialBankAccounts = BANK_ACCOUNTS.find(
    (account) => account.accountId === accountId
  )?.bankAccounts || [];

  // Última rifa de la cuenta (INACTIVE más reciente) para precargar sus cuentas bancarias
  const { data: lastRaffle } = useGetLastActiveRaffleQuery(accountId, {
    skip: accountId === "default" || accountId === "",
  });

  // Prioridad: lo ya editado en el formulario > cuentas de la última rifa > constante hardcodeada
  const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>(() =>
    resource.bankAccounts && resource.bankAccounts.length > 0
      ? resource.bankAccounts
      : lastRaffle?.bankAccounts?.length
        ? lastRaffle.bankAccounts
        : initialBankAccounts
  );

  const hadResourceAccountsRef = useRef(
    Boolean(resource.bankAccounts && resource.bankAccounts.length > 0)
  );
  const touchedRef = useRef(false);

  // Si la última rifa llega después del montaje, precargar sus cuentas
  // siempre que el usuario no haya editado nada todavía
  useEffect(() => {
    if (
      lastRaffle?.bankAccounts?.length &&
      !hadResourceAccountsRef.current &&
      !touchedRef.current
    ) {
      setBankAccounts(lastRaffle.bankAccounts);
    }
  }, [lastRaffle]);

  const [selectedTicket, setSelectedTicket] = useState<number>(3);
  const [priceInputs, setPriceInputs] = useState<{ [key: number]: string }>({});
  const [sourceRaffleId, setSourceRaffleId] = useState("");

  // Rifas de la cuenta (activas y pasadas) que tienen cuentas bancarias, de la más nueva a la más vieja
  const { data: raffles = [] } = useGetRafflesQuery(undefined);
  const rafflesWithBankAccounts = useMemo(
    () =>
      raffles
        .filter((raffle) => raffle.accountId === accountId && raffle.bankAccounts?.length)
        .sort((a, b) => dayjs(b.createdDate).valueOf() - dayjs(a.createdDate).valueOf()),
    [raffles, accountId]
  );

  const handleCopyFromRaffle = (raffleId: string) => {
    const raffle = rafflesWithBankAccounts.find(({ id }) => id === raffleId);
    if (!raffle) return;
    touchedRef.current = true;
    setSourceRaffleId(raffleId);
    setPriceInputs({});
    setBankAccounts(cloneBankAccounts(raffle.bankAccounts));
  };

  useEffect(() => {
    setResource((prevResource) => ({
      ...prevResource,
      bankAccounts
    }));
    
    // Validar que haya al menos una cuenta bancaria
    if (setDisableNext !== undefined) {
      setDisableNext(bankAccounts.length === 0);
    }
  }, [bankAccounts, setResource, setDisableNext]);

  const handleAccountChange = (index: number, field: keyof BankAccountItem, value: any) => {
    touchedRef.current = true;
    const updatedAccounts = [...bankAccounts];
    if (field === 'ticketPriceAmount') {
      // Permitir solo números y un punto decimal
      const numericValue = value.toString().replace(/[^0-9.]/g, '');
      // Asegurar que solo haya un punto decimal
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 
        ? parts[0] + '.' + parts.slice(1).join('') 
        : numericValue;
      
      // Guardar el valor como string en el estado local para permitir puntos mientras se escribe
      setPriceInputs(prev => ({ ...prev, [index]: validValue }));
      
      // Convertir a número solo si el valor es completo (no termina en punto)
      const numValue = validValue === '' || validValue === '.' 
        ? 0 
        : Number.parseFloat(validValue) || 0;
      
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        [field]: numValue
      };
    } else if (field === 'bankId') {
      // Si cambia el banco, actualizar también el nombre del banco
      const foundedBank = banks.find(bank => bank.id === value);
      if (foundedBank) {
        updatedAccounts[index] = {
          ...updatedAccounts[index],
          bankId: value,
          bank: foundedBank.name
        };
      }
    } else {
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        [field]: value
      };
    }
    setBankAccounts(updatedAccounts);
  };

  const handleConversionChange = (index: number, field: 'dolarPrice' | 'dolarPriceCurrency', value: any) => {
    touchedRef.current = true;
    const updatedAccounts = [...bankAccounts];
    updatedAccounts[index].conversion ??= { dolarPrice: 0, dolarPriceCurrency: '' };
    updatedAccounts[index] = {
      ...updatedAccounts[index],
      conversion: {
        ...updatedAccounts[index].conversion,
        [field]: field === 'dolarPrice' ? Number.parseInt(value) || 0 : value
      }
    };
    setBankAccounts(updatedAccounts);
  };

  const handleAddBankAccount = () => {
    touchedRef.current = true;
    // Obtener el primer banco disponible como valor por defecto
    const defaultBank = banks.length > 0 ? banks[0] : null;
    
    const newAccount: BankAccountItem = {
      bankId: defaultBank?.id as any,
      bank: defaultBank?.name || '',
      accountType: 'Cuenta Corriente',
      name: '',
      dni: '',
      accountNumber: '',
      phone: '',
      ticketPrice: '',
      ticketPriceAmount: 0,
      ticketCurrency: 'CLP',
    };
    
    setBankAccounts([...bankAccounts, newAccount]);
  };

  const handleDeleteBankAccount = (index: number) => {
    touchedRef.current = true;
    const updatedAccounts = bankAccounts.filter((_, i) => i !== index);
    setBankAccounts(updatedAccounts);
  };

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* Copiar cuentas bancarias de otra rifa */}
        {rafflesWithBankAccounts.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Tomar cuentas de otra rifa
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reemplaza las cuentas de abajo por las de la rifa que elijas. Después puedes editarlas.
              </Typography>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Rifa</InputLabel>
                <Select
                  value={sourceRaffleId}
                  onChange={(e) => handleCopyFromRaffle(e.target.value)}
                  label="Rifa"
                  renderValue={(raffleId) => rafflesWithBankAccounts.find(({ id }) => id === raffleId)?.name ?? ""}
                >
                  {rafflesWithBankAccounts.map((raffle) => (
                    <MenuItem key={raffle.id} value={raffle.id}>
                      <Box>
                        <Typography>{raffle.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {raffle.createdDate ? `${dayjs(raffle.createdDate).format("DD/MM/YYYY")} • ` : ""}
                          {raffle.bankAccounts.length} {raffle.bankAccounts.length === 1 ? "cuenta" : "cuentas"}
                          {raffle.status === "ACTIVE" ? " • Activa" : ""}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>
        )}

        {/* Selector de Cantidad de Tickets */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
              Cantidad de Tickets
            </Typography>
            <Box display="flex" gap={2}>
              {[3, 5, 10].map((count) => (
                <Chip
                  key={count}
                  label={`${count} Tickets`}
                  onClick={() => setSelectedTicket(count)}
                  color={selectedTicket === count ? "primary" : "default"}
                  variant={selectedTicket === count ? "filled" : "outlined"}
                  sx={{
                    fontSize: '1rem',
                    padding: '8px 16px',
                    height: 'auto',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Lista de Cuentas Bancarias */}
        {bankAccounts.map((bankAccount, index) => {
          const bank = banks.find(b => b.id === bankAccount.bankId);
          const accountKey = `${bankAccount.bankId}-${bankAccount.accountNumber}-${index}`;
          
          return (
            <Grid item xs={12} key={accountKey}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                {/* Header de la Cuenta */}
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  {bank && getIconFromBankId({
                    bankId: bank.id,
                    width: 40,
                    height: 40,
                  })}
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="600">
                      {bank?.name || bankAccount.bank || 'Nueva Cuenta Bancaria'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {bankAccount.accountType || 'Seleccione tipo'} • {bankAccount.accountNumber || 'Número de cuenta'}
                    </Typography>
                  </Box>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteBankAccount(index)}
                    aria-label="eliminar cuenta bancaria"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Campos Editables */}
                <Grid container spacing={3}>
                  {/* Información del Banco */}
                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Banco</InputLabel>
                      <Select
                        value={bankAccount.bankId || ""}
                        onChange={(e) => handleAccountChange(index, 'bankId', e.target.value)}
                        label="Banco"
                      >
                        {banks.map((bank) => (
                          <MenuItem key={bank.id} value={bank.id}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {getIconFromBankId({
                                bankId: bank.id,
                                width: 20,
                                height: 20,
                              })}
                              <Typography>{bank.name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Tipo de cuenta</InputLabel>
                      <Select
                        value={bankAccount.accountType || ""}
                        onChange={(e) => handleAccountChange(index, 'accountType', e.target.value)}
                        label="Tipo de cuenta"
                      >
                        {[
                          "Cuenta Corriente",
                          "Cuenta RUT",
                          "Cuenta Vista",
                          "Cuenta de Ahorro",
                          "Nequi",
                          "Zelle",
                          "Binance",   
                          "Pago Móvil",
                        ].map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Información Personal */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nombre del titular"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.name || ""}
                      onChange={(e) => handleAccountChange(index, 'name', e.target.value)}
                      helperText="Nombre completo del titular"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="DNI / Cédula / RUT"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.dni || ""}
                      onChange={(e) => handleAccountChange(index, 'dni', e.target.value)}
                      helperText="Documento de identidad"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Número de cuenta"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.accountNumber || ""}
                      onChange={(e) => handleAccountChange(index, 'accountNumber', e.target.value)}
                      helperText="Número de cuenta bancaria"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Teléfono"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.phone || ""}
                      onChange={(e) => handleAccountChange(index, 'phone', e.target.value)}
                      helperText="Número de teléfono (opcional)"
                    />
                  </Grid>

                  {/* Precios */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Precio (texto)"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.ticketPrice || ""}
                      onChange={(e) => handleAccountChange(index, 'ticketPrice', e.target.value)}
                      helperText="Ejemplo: $10.000"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Precio (número)"
                      variant="outlined"
                      fullWidth
                      type="text"
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: String.raw`[0-9]*\.?[0-9]*`
                      }}
                      value={(() => {
                        if (priceInputs[index] !== undefined) {
                          return priceInputs[index];
                        }
                        if (bankAccount.ticketPriceAmount != null) {
                          return bankAccount.ticketPriceAmount.toString();
                        }
                        return "";
                      })()}
                      onChange={(e) => handleAccountChange(index, 'ticketPriceAmount', e.target.value)}
                      onBlur={() => {
                        // Al perder el foco, limpiar el estado local si el valor es válido
                        const currentValue = priceInputs[index];
                        if (currentValue && !currentValue.endsWith('.')) {
                          setPriceInputs(prev => {
                            const newState = { ...prev };
                            delete newState[index];
                            return newState;
                          });
                        }
                      }}
                      helperText="Solo números, admite decimales (ej: 5.56, 254.2)"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Moneda"
                      variant="outlined"
                      fullWidth
                      value={bankAccount.ticketCurrency || ""}
                      onChange={(e) => handleAccountChange(index, 'ticketCurrency', e.target.value)}
                      helperText="Ejemplo: CLP, USD, EUR"
                    />
                  </Grid>

                  {/* Campos de Conversión */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        ¿Tiene conversión?
                      </Typography>
                      <Button
                        size="small"
                        variant={bankAccount.conversion ? "contained" : "outlined"}
                        onClick={() => {
                          const updatedAccounts = [...bankAccounts];
                          if (bankAccount.conversion) {
                            updatedAccounts[index] = {
                              ...updatedAccounts[index],
                              conversion: undefined
                            };
                          } else {
                            updatedAccounts[index] = {
                              ...updatedAccounts[index],
                              conversion: { dolarPrice: 0, dolarPriceCurrency: '' }
                            };
                          }
                          setBankAccounts(updatedAccounts);
                        }}
                      >
                        {bankAccount.conversion ? "Sí" : "No"}
                      </Button>
                    </Box>
                  </Grid>

                  {bankAccount.conversion && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Precio del dólar"
                          variant="outlined"
                          fullWidth
                          type="number"
                          value={bankAccount.conversion.dolarPrice || ""}
                          onChange={(e) => handleConversionChange(index, 'dolarPrice', e.target.value)}
                          helperText="Precio de conversión"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Moneda del dólar"
                          variant="outlined"
                          fullWidth
                          value={bankAccount.conversion.dolarPriceCurrency || ""}
                          onChange={(e) => handleConversionChange(index, 'dolarPriceCurrency', e.target.value)}
                          helperText="Ejemplo: USD, EUR"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        {/* Botón para agregar cuenta bancaria */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddBankAccount}
            sx={{
              py: 2,
              borderStyle: 'dashed',
              borderWidth: 2,
              '&:hover': {
                borderStyle: 'dashed',
                borderWidth: 2,
              }
            }}
          >
            Agregar Cuenta Bancaria
          </Button>
        </Grid>

        {bankAccounts.length === 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No hay cuentas bancarias configuradas.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Haz clic en "Agregar Cuenta Bancaria" para comenzar.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
